// Confirmation before a destructive or outward action. Put data-confirm="message" on a submit
// button, a form or a link; optional data-confirm-title, -type (danger|warning|info|success),
// -action (button label), -option (checkbox label) and -option-name (posted as "true" when
// ticked). data-confirm-details="#id" copies that element's content into the dialog, under the
// message, for a confirmation that must show what is being confirmed (the Inbox's bulk approve). The dialog is #confirm-modal in the layout. window.ConfirmModal.show(...) returns a
// promise for script callers. A danger confirmation opens with Cancel focused, so Enter
// can't destroy something by accident; the others focus the action.
import { $ } from './dom';

type Kind = 'danger' | 'warning' | 'info' | 'success';
interface Request {
	title: string;
	message: string;
	type: Kind;
	action: string;
	option?: string;
	details?: string;
}

const TITLES: Record<Kind, string> = {
	danger: 'Are you sure?',
	warning: 'Please confirm',
	info: 'Confirm',
	success: 'Confirm',
};
const ICONS: Record<Kind, string> = {
	danger: 'alert-triangle',
	warning: 'alert-circle',
	info: 'info-circle',
	success: 'circle-check',
};

function ask(req: Request): Promise<{ ok: boolean; option: boolean }> {
	const dialog = $<HTMLDialogElement>('#confirm-modal');
	if (!dialog) return Promise.resolve({ ok: window.confirm(req.message), option: false });
	$('#confirm-modal-title')!.textContent = req.title;
	$('#confirm-modal-message')!.textContent = req.message;
	const iconUse = $('#confirm-modal-icon use');
	iconUse?.setAttribute('href', (iconUse.getAttribute('href') ?? '').replace(/#[\w-]+$/, '#' + ICONS[req.type]));
	$('#confirm-modal-icon')?.setAttribute(
		'data-tone',
		req.type === 'danger' ? 'bad' : req.type === 'warning' ? 'gate' : req.type === 'success' ? 'ok' : 'info',
	);
	const confirm = $<HTMLButtonElement>('#confirm-modal-confirm')!;
	confirm.textContent = req.action;
	confirm.dataset.variant = req.type === 'danger' ? 'danger' : 'primary';
	const details = $<HTMLElement>('#confirm-modal-details');
	if (details) {
		const source = req.details ? $<HTMLElement>(req.details) : null;
		details.replaceChildren(...(source ? [...source.childNodes].map((n) => n.cloneNode(true)) : []));
		details.hidden = !source || source.childNodes.length === 0;
	}
	const optionBox = $('#confirm-modal-option-box')!;
	const option = $<HTMLInputElement>('#confirm-modal-option')!;
	optionBox.hidden = !req.option;
	option.checked = false;
	if (req.option) $('#confirm-modal-option-label')!.textContent = req.option;

	return new Promise((resolve) => {
		const done = (ok: boolean) => {
			dialog.removeEventListener('close', onClose);
			confirm.removeEventListener('click', onConfirm);
			if (dialog.open) dialog.close();
			details?.replaceChildren();
			resolve({ ok, option: option.checked });
		};
		const onConfirm = () => done(true);
		const onClose = () => done(false);
		confirm.addEventListener('click', onConfirm);
		dialog.addEventListener('close', onClose);
		dialog.showModal();
		(req.type === 'danger' ? ($<HTMLButtonElement>('#confirm-modal-cancel') ?? confirm) : confirm).focus();
	});
}

function requestFrom(el: Element, fallback?: Element | null): Request {
	const read = (name: string) => el.getAttribute(name) ?? fallback?.getAttribute(name) ?? undefined;
	const type = (read('data-confirm-type') ?? 'warning') as Kind;
	return {
		message: read('data-confirm') ?? 'Are you sure?',
		title: read('data-confirm-title') ?? TITLES[type] ?? TITLES.warning,
		type,
		action: read('data-confirm-action') ?? 'Confirm',
		option: read('data-confirm-option'),
		details: read('data-confirm-details'),
	};
}

export function initConfirm(): void {
	document.addEventListener(
		'submit',
		async (e) => {
			const form = e.target as HTMLFormElement;
			const submitter = (e as SubmitEvent).submitter as HTMLButtonElement | null;
			const source = submitter?.hasAttribute('data-confirm')
				? submitter
				: form.hasAttribute('data-confirm')
					? form
					: null;
			if (!source || form.hasAttribute('data-confirm-approved')) {
				form.removeAttribute('data-confirm-approved');
				return;
			}
			e.preventDefault();
			e.stopImmediatePropagation();
			const req = requestFrom(source, source === submitter ? form : null);
			const { ok, option } = await ask(req);
			if (!ok) return;
			if (req.option) {
				const name =
					source.getAttribute('data-confirm-option-name') ??
					form.getAttribute('data-confirm-option-name') ??
					'confirmOption';
				form.querySelector(`input[type=hidden][name="${name}"][data-confirm-generated]`)?.remove();
				if (option) {
					const hidden = document.createElement('input');
					Object.assign(hidden, { type: 'hidden', name, value: 'true' });
					hidden.dataset.confirmGenerated = '';
					form.appendChild(hidden);
				}
			}
			form.setAttribute('data-confirm-approved', '');
			form.requestSubmit(submitter ?? undefined);
		},
		true,
	);

	document.addEventListener(
		'click',
		async (e) => {
			const link = (e.target as Element).closest<HTMLAnchorElement>('a[data-confirm]');
			if (!link) return;
			e.preventDefault();
			e.stopImmediatePropagation();
			const { ok } = await ask(requestFrom(link));
			if (ok) location.href = link.href;
		},
		true,
	);

	window.ConfirmModal = {
		show: async (title: string, message: string, type: Kind = 'warning', action = 'Confirm') =>
			(await ask({ title, message, type, action })).ok,
	};
}
